import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";
import { getRelated } from "@/utils/movieQuery";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  const movie = movies.find((item) => item.id === id);

  useSeo({
    title: movie
      ? `${movie.title} - 饥渴的岳中文字幕资源详情`
      : "影片不存在 - 饥渴的岳中文字幕",
    description: movie
      ? `《${movie.title}》（${movie.year}，${movie.region}）${movie.genres.join("、")}类型，${movie.version}。${movie.description}`
      : "未找到对应的《饥渴的岳》中文字幕影片资源。",
    keywords: movie ? `${movie.title},饥渴的岳,中文字幕,${movie.genres.join(",")}` : undefined,
  });

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-8 py-24 flex flex-col items-center justify-center text-center min-h-[60vh]">
        <span className="w-20 h-20 rounded-full bg-background-900 text-foreground-500 flex items-center justify-center text-4xl mb-6">
          <i className="ri-file-warning-line" />
        </span>
        <h1 className="font-heading text-foreground-50 text-2xl font-bold mb-3">影片不存在</h1>
        <p className="text-sm text-foreground-400 font-label mb-8 max-w-md">
          抱歉，你访问的影片 ID「{id}」没有对应的资源，可能链接已失效或地址有误。
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 font-label text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
          <Link
            to="/category"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-700 hover:border-accent-600 hover:text-accent-400 text-foreground-100 font-label text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-list-check-2" />
            浏览全部影片
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(movie.id);
  const related = getRelated(movie, movies, 8);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制到剪贴板，快分享给朋友吧");
    } catch {
      setToast("复制失败，请手动复制浏览器地址栏链接");
    }
    window.setTimeout(() => setToast(""), 2600);
  };

  const handleFavorite = () => {
    const next = toggleFavorite(movie.id);
    setToast(next ? "已加入我的收藏" : "已从收藏中移除");
    window.setTimeout(() => setToast(""), 2200);
  };

  return (
    <div className="relative">
      <div className="absolute inset-x-0 top-0 h-[420px] md:h-[520px] overflow-hidden">
        <img
          src={movie.cover}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-top opacity-25 blur-sm scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/60 via-background-950/85 to-background-950" />
      </div>

      <div className="relative w-full px-4 md:px-8 py-8 md:py-12">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 font-label mb-6 flex-wrap">
          <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/category" className="hover:text-accent-400 transition-colors cursor-pointer">
            分类
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-200 line-clamp-1">{movie.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] xl:grid-cols-[340px_1fr] gap-6 lg:gap-10">
          <div className="mx-auto lg:mx-0 w-full max-w-[260px] lg:max-w-none">
            <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-800 bg-background-900">
              <img
                src={movie.cover}
                alt={`${movie.title}（${movie.year}）中文字幕电影海报`}
                title={`${movie.title} 中文字幕`}
                className="w-full h-full object-top"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex items-center justify-center gap-2 h-11 rounded-md font-label text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  favorited
                    ? "bg-primary-600 hover:bg-primary-500 text-background-50"
                    : "border border-background-700 hover:border-accent-600 hover:text-accent-400 text-foreground-100"
                }`}
              >
                <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center justify-center gap-2 h-11 rounded-md border border-background-700 hover:border-accent-600 hover:text-accent-400 text-foreground-100 font-label text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
            </div>
          </div>

          <div className="min-w-0">
            <h1 className="font-heading text-foreground-50 text-2xl md:text-4xl font-bold mb-4">
              {movie.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-accent-500 text-background-950 text-sm font-label font-medium whitespace-nowrap">
                <i className="ri-star-fill" />
                {movie.rating.toFixed(1)} 分
              </span>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-background-900 border border-background-800 text-foreground-200 text-sm font-label whitespace-nowrap">
                <i className="ri-calendar-line" />
                {movie.year}
              </span>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-background-900 border border-background-800 text-foreground-200 text-sm font-label whitespace-nowrap">
                <i className="ri-earth-line" />
                {movie.region}
              </span>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-primary-950 border border-primary-800 text-primary-200 text-sm font-label whitespace-nowrap">
                <i className="ri-translate-2" />
                {movie.version}
              </span>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-background-900 border border-background-800 text-foreground-200 text-sm font-label whitespace-nowrap">
                <i className="ri-time-line" />
                {movie.duration}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-8 text-sm font-label">
              <div className="flex gap-2">
                <span className="text-foreground-500 shrink-0 w-16">导演</span>
                <span className="text-foreground-100">{movie.director}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-foreground-500 shrink-0 w-16">中文字幕</span>
                <span className="text-foreground-100">{movie.subtitle}</span>
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <span className="text-foreground-500 shrink-0 w-16">主演</span>
                <span className="text-foreground-100">{movie.actors.join(" / ")}</span>
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <span className="text-foreground-500 shrink-0 w-16">类型</span>
                <span className="flex flex-wrap gap-2">
                  {movie.genres.map((g) => (
                    <Link
                      key={g}
                      to={`/category?genre=${encodeURIComponent(g)}`}
                      className="px-2 py-0.5 rounded bg-secondary-900/60 text-secondary-100 hover:text-accent-400 transition-colors cursor-pointer"
                    >
                      {g}
                    </Link>
                  ))}
                </span>
              </div>
            </div>

            <section className="mb-8">
              <h2 className="font-heading text-foreground-50 text-lg md:text-xl font-bold mb-3 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                剧情简介
              </h2>
              <p className="text-sm md:text-base text-foreground-300 font-label leading-relaxed">
                {movie.description}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-foreground-50 text-lg md:text-xl font-bold mb-3 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                可播放资源
              </h2>
              <div className="rounded-lg border border-dashed border-background-700 bg-background-900/60 p-5 md:p-6">
                <div className="flex items-start gap-4">
                  <span className="w-12 h-12 rounded-full bg-background-800 text-foreground-400 flex items-center justify-center text-2xl shrink-0">
                    <i className="ri-play-circle-line" />
                  </span>
                  <div>
                    <p className="text-foreground-100 font-label text-sm md:text-base mb-1.5">
                      演示说明：本站为影视内容展示站点，暂未接入真实视频播放接口。
                    </p>
                    <p className="text-foreground-400 font-label text-xs md:text-sm leading-relaxed">
                      该区域用于展示「{movie.title}」可用的中文字幕资源位（{movie.version}）。后续可在此基础上接入正版播放能力，目前仅作界面示意，请勿视为可直接观看。
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-foreground-50 text-lg md:text-xl font-bold mb-3 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                影片标签
              </h2>
              <div className="flex flex-wrap gap-2">
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-900 border border-background-800 text-foreground-300 text-xs md:text-sm font-label"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </section>
          </div>
        </div>

        <section className="mt-14 md:mt-20">
          <div className="flex items-end justify-between gap-4 mb-6">
            <h2 className="font-heading text-foreground-50 text-xl md:text-2xl font-bold flex items-center gap-3">
              <span className="w-1 h-6 rounded-full bg-accent-500" />
              相关推荐
            </h2>
            <Link
              to="/category"
              className="text-sm text-foreground-300 hover:text-accent-400 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 font-label shrink-0"
            >
              更多影片
              <i className="ri-arrow-right-s-line" />
            </Link>
          </div>
          <div className="flex gap-3 md:gap-5 overflow-x-auto pb-3 no-scrollbar">
            {related.map((item) => (
              <div key={item.id} className="w-[150px] sm:w-[180px] md:w-[200px] shrink-0">
                <MovieCard movie={item} />
              </div>
            ))}
          </div>
        </section>

        <div className="mt-12 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-700 hover:border-accent-600 hover:text-accent-400 text-foreground-200 font-label text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            返回上一页
          </button>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md bg-accent-500 text-background-950 font-label text-sm animate-fade-up max-w-[90vw] text-center">
          {toast}
        </div>
      )}
    </div>
  );
}